import { Fragment, MutableRefObject } from 'react';
import React from 'react';

import { capitalizeFirstLetter } from 'utils/formattingUtil';

import { DEFAULT_LANGUAGE, LANGUAGE_STATUS } from 'components/LanguagePicker/constants';
import { LanguageStatusType } from 'components/LanguagePicker/types';

import { EMPTY_TRANSLATABLE_VALUES, TRANSLATABLE_FIELDS } from './constants';
import { InitialValuesParams, LanguageStatus, LanguageSwitchParams, TranslationData, TranslationDraftsMap} from './types';


export const saveDraftToRef = (
  draftsRef: MutableRefObject<TranslationDraftsMap>,
  languageCode: string,
  formData: any,
  debug = false
) => {
  const draftData = {} as TranslationData;
  
  TRANSLATABLE_FIELDS.forEach(field => {
    const value = formData[field];
    if (value !== undefined && value !== null) {
      draftData[field] = value;
    }
  });
  
  draftsRef.current[languageCode] = draftData;
};

const mergeTranslatableFields = (
  target: any,
  source: any
): void => {
  if (source && Object.keys(source).length > 0) {
    TRANSLATABLE_FIELDS.forEach(field => {
      if (source[field] !== undefined) {
        target[field] = source[field];
      }
    });
  }
};

export const handleLanguageSwitch = (params: LanguageSwitchParams): any => {
  const {
    currentLanguage,
    newLanguage,
    draftsRef,
    currentFormData,
    currentContentsData,
    initialValues
  } = params;

  saveDraftToRef(draftsRef, currentLanguage, currentFormData);
  
  const newLanguageDraftData = draftsRef.current[newLanguage] ?? {};

  const serverData = currentContentsData?.translations?.find(
    (t: any) => t.isoCode === newLanguage
  ) || {};

  const isEditingMode = !!currentContentsData;
  const hasServerDataForNewLanguage = Object.keys(serverData).length > 0;
  
  let mergedData: any;
  
  if (isEditingMode && !hasServerDataForNewLanguage) {
    mergedData = { ...initialValues, ...EMPTY_TRANSLATABLE_VALUES };
  } else {
    mergedData = { ...initialValues };
  }
  
  mergeTranslatableFields(mergedData, serverData);
  mergeTranslatableFields(mergedData, newLanguageDraftData);
  
  return mergedData;
};



export const hasNonEmptyDraftContent = (draft: TranslationData): boolean => {
  return !!(
    draft.title?.trim() ||
    (draft.labels?.length > 0) ||
    draft.headerImage?.trim() ||
    draft.thumbnailImage?.trim()
  );
};

const createTranslationObject = (languageCode: string, data: any) => ({
  isoCode: languageCode,
  title: data.title || '',
  subTitle: data.subTitle || '',
  headerImage: data.headerImage || '',
  thumbnailImage: data.thumbnailImage || '',
  bodyText: data.bodyText || '',
  labels: data.labels || []
});

export const collectAllTranslations = (
  currentLanguage: string,
  currentFormData: any,
  draftsRef: MutableRefObject<TranslationDraftsMap>
): any[] => {
  const translations = [];
  translations.push(createTranslationObject(currentLanguage, currentFormData));
  Object.entries(draftsRef.current).forEach(([languageCode, draftData]) => {
    if (languageCode !== currentLanguage && hasNonEmptyDraftContent(draftData)) {
      translations.push(createTranslationObject(languageCode, draftData));
    }
  });
  
  return translations;
};

export const validateAllLanguagesComplete = (languageStatuses: any): boolean => {
  return Object.values(languageStatuses).every(status => 
    status === LANGUAGE_STATUS.COMPLETE
  );
};

export const calculateAllLanguageStatuses = (
  currentLanguage: string,
  currentFormData: any,
  draftsRef: MutableRefObject<TranslationDraftsMap>,
  serverTranslations?: any[]
): any => {
  const statuses: any = {};
  
  let currentStatus = calculateLanguageStatus(currentFormData);
  const currentDraft = draftsRef.current[currentLanguage];
  if (currentStatus === LANGUAGE_STATUS.EMPTY && currentDraft && hasNonEmptyDraftContent(currentDraft)) {
    currentStatus = LANGUAGE_STATUS.PARTIAL;
  }
  statuses[currentLanguage] = currentStatus;
  
  Object.entries(draftsRef.current).forEach(([languageCode, draftData]) => {
    if (languageCode !== currentLanguage) {
      let status = calculateLanguageStatus(draftData);
      if (status === LANGUAGE_STATUS.EMPTY && hasNonEmptyDraftContent(draftData)) {
        status = LANGUAGE_STATUS.PARTIAL;
      }
      statuses[languageCode] = status;
    }
  });
  
  if (serverTranslations) {
    serverTranslations.forEach((translation: any) => {
      if (!statuses[translation.isoCode]) {
        const status = calculateLanguageStatus(translation);
        statuses[translation.isoCode] = status;
      }
    });
  }
  
  return statuses;
};

export const prepareSubmissionData = (
  formData: any,
  translations: any[],
  isEditing: boolean = false
): any => {
  const { isPublishDateEnabled, localization, ...submitData } = formData;
  (submitData as any).translations = translations;
  
  if (submitData.state === 'visible') {
    submitData.publishDate = new Date().toISOString();
  } else if (submitData.state === 'hidden') {
    if (isPublishDateEnabled && submitData.publishDate) {
      submitData.state = 'scheduled';
    } else {
      delete submitData.publishDate;
    }
  }
  
  if (isEditing && submitData.publishDate) {
    submitData.publishDate = new Date().toISOString();
  }
  
  return submitData;
};

export const prepareContentSubmission = (
  currentLanguage: string,
  formData: any,
  translationDraftsRef: any,
  languageStatus: LanguageStatus,
  isEditing: boolean = false
) => {
  const allTranslations = collectAllTranslations(currentLanguage, formData, translationDraftsRef);
  
  const translations = allTranslations.filter(translation => {
    const status = languageStatus[translation.isoCode];
    return translation.isoCode !== DEFAULT_LANGUAGE.value && status === LANGUAGE_STATUS.COMPLETE;
  });
  
  return prepareSubmissionData(formData, translations, isEditing);
};

export const updateLanguageStatus = (
  draftsRef: MutableRefObject<TranslationDraftsMap>,
  currentLanguage: string,
  formData: any,
  setLanguageStatus: React.Dispatch<React.SetStateAction<any>>
): void => {
  let currentStatus = calculateLanguageStatus(formData);
  
  const draft = draftsRef.current[currentLanguage];
  if (currentStatus === LANGUAGE_STATUS.EMPTY && draft && hasNonEmptyDraftContent(draft)) {
    currentStatus = LANGUAGE_STATUS.PARTIAL;
  }
  
  setLanguageStatus((prev: any) => ({
    ...prev,
    [currentLanguage]: currentStatus
  }));
};

export const calculateLanguageStatus = (formData: TranslationData): LanguageStatusType => {
  const requiredFields = [
    formData.title,
    formData.subTitle,
    formData.bodyText,
    formData.labels?.length > 0,
    formData.headerImage,
    formData.thumbnailImage
  ];

  const completedFields = requiredFields.filter(field => {
    if (typeof field === 'string') {
      return field && field.trim().length > 0;
    }
    if (typeof field === 'boolean') {
      return field;
    }
    return false;
  });

  if (completedFields.length === 0) {
    return LANGUAGE_STATUS.EMPTY;
  } else if (completedFields.length === requiredFields.length) {
    return LANGUAGE_STATUS.COMPLETE;
  } else {
    return LANGUAGE_STATUS.PARTIAL;
  }
};

export const getInitialFormValues = (params: InitialValuesParams): any => {
  const {
    currentContentsData,
    dup = false,
    EContentRadioButtonOptions,
    EContentTypeOptions,
    EVisibilityOptions,
    DEFAULT_LANGUAGE
  } = params;

  let state = currentContentsData?.state ?? EContentRadioButtonOptions.HIDDEN;
  let isPublishDateEnabled = false;

  if (
    (currentContentsData?.state === EContentRadioButtonOptions.SCHEDULED &&
      currentContentsData?.publishDate) ||
    (currentContentsData?.state === EContentRadioButtonOptions.HIDDEN &&
      currentContentsData?.publishDate)
  ) {
    isPublishDateEnabled = true;
    if (currentContentsData?.state === EContentRadioButtonOptions.SCHEDULED) {
      state = EContentRadioButtonOptions.HIDDEN;
    }
  }

  return {
    title:
      (dup
        ? `${currentContentsData?.title}_duplicated`
        : currentContentsData?.title) ?? '',
    subTitle: currentContentsData?.subTitle ?? '',
    contentType: currentContentsData?.contentType ?? EContentTypeOptions.NEWS,
    bodyText: currentContentsData?.bodyText ?? '',
    headerImage: currentContentsData?.headerImage ?? '',
    thumbnailImage: currentContentsData?.thumbnailImage ?? '',
    pages:
      currentContentsData?.pages?.map((page: { id: string }) => page.id) ??
      [],
    labels: currentContentsData?.labels ?? [],
    visibility: currentContentsData?.visibility ?? EVisibilityOptions.PUBLIC,
    state,
    slug: currentContentsData?.slug ?? '',
    publishDate: currentContentsData?.publishDate ?? '',
    isPublishDateEnabled,
    localization: DEFAULT_LANGUAGE
  };
};


export const getEnabledUniqueLanguages = (languagesData: any[] | undefined): any[] => {
  if (!languagesData?.length) return [];
  
  const uniqueLanguages = new Map();
  for (const language of languagesData) {
    if (!language.isEnabled) continue;
    const key = `${language.language}-${language.isoCode}`;
    if (!uniqueLanguages.has(key)) {
      uniqueLanguages.set(key, language);
    }
  }
  
  return Array.from(uniqueLanguages.values());
};

export const shouldShowLanguagePicker = (languagesData: any[] | undefined): boolean => {
  const enabledLanguages = getEnabledUniqueLanguages(languagesData);
  return enabledLanguages.length > 1;
};

export const isDefaultLanguage = (languageCode: string | undefined): boolean => {
  return languageCode === DEFAULT_LANGUAGE.value;
};

export const getDefaultSelectedLanguage = (
  currentValue: string,
  enabledLanguages: any[]
): { label: string; value: string } => {
  const currentLanguageValue = currentValue || DEFAULT_LANGUAGE.value;
  const foundLanguage = enabledLanguages.find(lang => lang.isoCode === currentLanguageValue);
  
  return foundLanguage 
    ? { label: foundLanguage.language, value: foundLanguage.isoCode } 
    : DEFAULT_LANGUAGE;
};

export const transformLanguageValue = (
  newValue: string,
  enabledLanguages: any[]
): { label: string; value: string } => {
  const selectedLanguage = enabledLanguages.find(lang => lang.isoCode === newValue);
  return selectedLanguage 
    ? { label: selectedLanguage.language, value: selectedLanguage.isoCode }
    : DEFAULT_LANGUAGE;
};

export const getIncompleteLanguages = (languageStatus: LanguageStatus, enabledLanguages: any[]): string[] => {
  const incompleteLanguages: string[] = [];
  
  enabledLanguages.forEach(lang => {
    const status = languageStatus[lang.isoCode];
    if (status && status !== LANGUAGE_STATUS.COMPLETE) {
      incompleteLanguages.push(lang.language || lang.isoCode);
    }
  });
  
  return incompleteLanguages;
};

export const createIncompleteTranslationsModalContent = (incompleteLanguages: string[]): React.ReactNode => {
  if (incompleteLanguages.length === 0) return null;
  
  const languageElements = incompleteLanguages.length === 1 
    ? <strong>{capitalizeFirstLetter(incompleteLanguages[0])}</strong>
    : incompleteLanguages.map((lang, index) => (
        <Fragment key={lang}>
          <strong>{capitalizeFirstLetter(lang)}</strong>
          {index < incompleteLanguages.length - 1 ? ', ' : ''}
        </Fragment>
      ));

  return (
    <>
      <p className="mb-2 text-base-mutedForeground">Some languages are missing required translation fields: {languageElements}.</p>
      <p className="text-base-mutedForeground">Do you want to save without completing them?</p>
    </>
  );
};